import React, { useState } from 'react'
import { enqueueSnackbar } from 'notistack'
import AiToolDownloadPdfButton from '../productionTools/AiToolDownloadPdfButton'
import { PRODUCER_TIP_TEXT } from '../../utils/opportunityEngine/constants'

const getScoreColor = (score) => {
  if (score >= 80) return 'var(--green)'
  if (score >= 60) return 'var(--gold)'
  return 'var(--blue)'
}

const OpportunityCard = ({ opp, index }) => {
  const p = opp.post
  const isHire = p.postType === 'hire'
  const sc = getScoreColor(opp.score)
  const location = [
    p.city !== 'Any City' ? p.city : null,
    p.country !== 'Any' ? p.country : null,
  ]
    .filter(Boolean)
    .join(', ')

  const openLink = () => window.open(p.link, '_blank')

  return (
    <div className="ocard">
      <div className="ohead">
        <div className="onum">{index + 1}</div>
        <div className="ohead-content">
          <div className="otitle">{p.pitchName}</div>
          <div className="otags">
            <span className={`ot ${isHire ? 'thire' : 'tpitch'}`}>
              {isHire ? '💼 Hire' : '🎬 Pitch'}
            </span>
            {location && <span className="ot tcity">📍 {location}</span>}
            <span className="ot tct">{p.contentType}</span>
            {p.isNew && <span className="ot tnew">New</span>}
          </div>
        </div>
      </div>

      <div className="obody">
        <div className="odesc">{p.description}</div>
        <a className="postlink" href={p.link} target="_blank" rel="noreferrer">
          🔗 {p.link}
        </a>
        <div className="mbar">
          <span className="mlbl">Match</span>
          <div className="mtrk">
            <div
              className="mfill"
              style={{
                width: `${opp.score}%`,
                background: `linear-gradient(90deg, ${sc}, ${sc}99)`,
              }}
            />
          </div>
          <span className="mpct">{opp.score}%</span>
        </div>
        {opp.whyFit && <div className="whyfit">🎯 {opp.whyFit}</div>}
        {opp.howToApply && (
          <div className="howbox">
            <div className="howlbl">→ How to Respond</div>
            <div className="howtxt">{opp.howToApply}</div>
          </div>
        )}
        {isHire && p.gender && p.gender !== 'Any' && (
          <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
            Requirements: {p.gender}
            {p.age ? `, Age ${p.age}` : ''}
          </div>
        )}
      </div>

      <div className="ofoot">
        <span className="oby">
          By {p.postedBy} · {p.postedDate}
        </span>
        <div className="oactions">
          {isHire ? (
            <button
              type="button"
              className="abtn abtn-apply"
              onClick={openLink}
            >
              Apply →
            </button>
          ) : (
            <>
              <button
                type="button"
                className="abtn abtn-teamup"
                onClick={openLink}
              >
                Team Up →
              </button>
              <button
                type="button"
                className="abtn abtn-hirenow"
                onClick={openLink}
              >
                Hire Now →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const OpportunityResults = ({ result, inputs, onReset }) => {
  const [pdfLoading, setPdfLoading] = useState(false)

  const metas = []
  if (inputs.country && inputs.country !== 'any')
    metas.push(`🌍 ${inputs.country}`)
  if (inputs.city && inputs.city !== 'any') metas.push(`📍 ${inputs.city}`)
  if (inputs.ct?.length > 0) metas.push(inputs.ct.join(', '))
  metas.push(new Date().toLocaleDateString('en-IN'))

  const handleDownloadPdf = async () => {
    setPdfLoading(true)
    try {
      const { downloadAiToolPdf } =
        await import('../../utils/aiTool/pdfDownload')
      const today = new Date().toLocaleDateString('en-IN').replace(/\//g, '-')
      await downloadAiToolPdf(inputs.aiuId, `PitchBlu-Opportunities-${today}`, {
        exportId: 'oe-pdf-export',
      })
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to download PDF. Please try again.',
        { variant: 'error' },
      )
    } finally {
      setPdfLoading(false)
    }
  }

  return (
    <div className="res-wrap on">
      <div className="rhead">
        <div>
          <div className="rtitle">
            Top Opportunities — {inputs.crafts.join(', ')}
          </div>
          <div className="rmetas">
            {metas.map((m) => (
              <span key={m} className="rmeta">
                {m}
              </span>
            ))}
          </div>
        </div>
        <div className="abtns">
          <AiToolDownloadPdfButton
            loading={pdfLoading}
            onClick={handleDownloadPdf}
          />
          <button type="button" className="btn-new" onClick={onReset}>
            + New Search
          </button>
        </div>
      </div>

      <div className="pulse">
        <div className="pi">
          <div className="dot db" />
          <span>Posts Scanned:</span>
          <span className="pv">{result.pulseTotal}</span>
        </div>
        <div className="pi">
          <div className="dot dg" />
          <span>Match Your Required Professions:</span>
          <span className="pv">{result.pulseMatch}</span>
        </div>
        <div className="pi">
          <div className="dot dy" />
          <span>Strong Matches:</span>
          <span className="pv">{result.pulseStrong}</span>
        </div>
      </div>

      {result.producerTip && (
        <div className="ptip on">
          <div className="ptip-t">💡 Note on Producers</div>
          <div className="ptip-b">{PRODUCER_TIP_TEXT}</div>
        </div>
      )}

      <div className="olist">
        {result.opps.map((opp, i) => (
          <OpportunityCard key={opp.post?.id || i} opp={opp} index={i} />
        ))}
      </div>

      <div className="advice">
        <div className="at">🎯 Your Strategy This Week</div>
        <div className="ab">{result.advice || '—'}</div>
      </div>
      <div className="weekly">
        🔁 Run this search every week — new posts come in daily. Apply within 48
        hours for the best response rate.
      </div>
    </div>
  )
}

export default OpportunityResults
